import { defineComponent, reactive, onMounted, watch, computed } from '@vue/composition-api';
import { HiddenAxios } from '@/functions/axios-functions';
import { SettingsContext } from '@/utilities/settings-context';
import Pagination from '@/components/pagination/pagination.vue';
import { debounce } from 'lodash';

// API response types

type CompanyResponse = {
  id: number;
  name: string;
  visibility: number;
};

type PaginatedCompanyResponse = {
  data: CompanyResponse[];
  total: number;
};

export default defineComponent({
  components: {
    Pagination,
  },
  setup(_, ctx) {
    const state = reactive({
      items: [] as (CompanyResponse & { editing: boolean; newVisibilityPercentage: number })[],
      total: 0,
      loading: false,
    });

    const currentPage = computed({
      get: () => ctx.root.$route.query.page ? parseInt(ctx.root.$route.query.page as string) : 1,
      set: (value: number) => {
        ctx.root.$router.push({ query: { ...(ctx.root.$route.query ?? {}), page: value.toString() } });
      },
    });

    const searchQuery = computed({
      get: () => ctx.root.$route.query.search?.toString() ?? '',
      set: (value: string) => {
        ctx.root.$router.push({ query: { ...(ctx.root.$route.query ?? {}), search: value } });
      },
    });

    const fetchData = async () => {
      state.loading = true;
      const response = await HiddenAxios.get<PaginatedCompanyResponse>(
        SettingsContext.getApiUrl('adminGetCompaniesVolumeVisibilityPercentage'),
        {
          params: {
            search: searchQuery.value,
            page: currentPage.value,
            pageSize: 10,
          },
        }
      );
      state.items = response.data.data.map(item => ({
        ...item,
        editing: false,
        newVisibilityPercentage: item.visibility,
      }));
      state.total = response.data.total;
      state.loading = false;
    };

    const updateVisibility = async (item: CompanyResponse & { editing: boolean; newVisibilityPercentage: number }, reset = false) => {
      const payload = {
        id: item.id,
        visibility: reset ? 100 : item.newVisibilityPercentage,
      };
      await HiddenAxios.post(
        SettingsContext.getApiUrl('adminSetVolumeSponsoredJobVisibility'),
        payload
      );
      item.visibility = payload.visibility;
      item.newVisibilityPercentage = payload.visibility;
      item.editing = false;
    };

    onMounted(fetchData);

    watch(() => [currentPage.value, searchQuery.value], debounce(fetchData, 500), { deep: true });

    return {
      state,
      fetchData,
      updateVisibility,
      currentPage,
      searchQuery
    };
  },
});
